import { useState, useEffect } from 'react'
import { Table, Card, Button, Space, Modal, Form, Input, Select, Tag, message, Popconfirm, Descriptions, Transfer } from 'antd'
import { PlusOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons'
import { deliveryDriverApi } from '../services/api'

const { Option } = Select

interface DriverZone {
  id: number
  district_code: string
  district_name: string
  city_name: string
  province_name: string
}

interface EmployeeInfo {
  id: number
  real_name: string
  phone: string
  employee_no: string
  status: number
}

interface DeliveryDriver {
  id: number
  employee_id: number
  is_active: number
  wecom_userid: string
  remark: string
  employee: EmployeeInfo
  zones: DriverZone[]
  created_at: string
}

// 上海区县列表（后续可扩展为从行政区划API获取）
const SHANGHAI_DISTRICTS = [
  { code: '310101', name: '黄浦区' },
  { code: '310104', name: '徐汇区' },
  { code: '310105', name: '长宁区' },
  { code: '310106', name: '静安区' },
  { code: '310107', name: '普陀区' },
  { code: '310109', name: '虹口区' },
  { code: '310110', name: '杨浦区' },
  { code: '310112', name: '闵行区' },
  { code: '310113', name: '宝山区' },
  { code: '310114', name: '嘉定区' },
  { code: '310115', name: '浦东新区' },
  { code: '310116', name: '金山区' },
  { code: '310117', name: '松江区' },
  { code: '310118', name: '青浦区' },
  { code: '310120', name: '奉贤区' },
  { code: '310151', name: '崇明区' },
]

export default function DeliveryDrivers() {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<DeliveryDriver[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [modalVisible, setModalVisible] = useState(false)
  const [detailVisible, setDetailVisible] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [currentDriver, setCurrentDriver] = useState<DeliveryDriver | null>(null)
  const [employees, setEmployees] = useState<EmployeeInfo[]>([])
  const [form] = Form.useForm()
  const [selectedZones, setSelectedZones] = useState<string[]>([])
  const [keyword, setKeyword] = useState('')

  useEffect(() => {
    loadData()
  }, [page, pageSize])

  const loadData = async () => {
    setLoading(true)
    try {
      const res = await deliveryDriverApi.getList({ page, page_size: pageSize, keyword })
      if (res.code === 200) {
        setData(res.data?.list || [])
        setTotal(res.data?.total || 0)
      }
    } catch (err) {
      message.error('加载配送员列表失败')
    } finally {
      setLoading(false)
    }
  }

  const loadEmployees = async () => {
    try {
      const res = await deliveryDriverApi.getAvailableEmployees()
      if (res.code === 200) {
        setEmployees(res.data || [])
      }
    } catch (err) {
      console.error('加载员工列表失败', err)
    }
  }

  const handleAdd = () => {
    setEditMode(false)
    setCurrentDriver(null)
    form.resetFields()
    setSelectedZones([])
    loadEmployees()
    setModalVisible(true)
  }

  const handleEdit = (record: DeliveryDriver) => {
    setEditMode(true)
    setCurrentDriver(record)
    form.setFieldsValue({
      employee_id: record.employee_id,
      wecom_userid: record.wecom_userid || '',
      remark: record.remark || '',
      is_active: record.is_active
    })
    setSelectedZones(record.zones?.map(z => z.district_code) || [])
    setModalVisible(true)
  }

  const handleDetail = (record: DeliveryDriver) => {
    setCurrentDriver(record)
    setDetailVisible(true)
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      const zones = selectedZones.map(code => {
        const d = SHANGHAI_DISTRICTS.find(item => item.code === code)
        return {
          district_code: code,
          district_name: d?.name || '',
          city_name: '上海市',
          province_name: '上海市'
        }
      })

      if (zones.length === 0) {
        message.warning('请至少选择一个配送区域')
        return
      }

      const payload = {
        ...values,
        zones
      }

      let res
      if (editMode && currentDriver) {
        res = await deliveryDriverApi.update(currentDriver.id, payload)
      } else {
        res = await deliveryDriverApi.create(payload)
      }

      if (res.code === 200) {
        message.success(editMode ? '更新成功' : '新增成功')
        setModalVisible(false)
        loadData()
      } else {
        message.error(res.message || '操作失败')
      }
    } catch (err) {
      console.error(err)
    }
  }

  const handleDelete = async (id: number) => {
    try {
      const res = await deliveryDriverApi.delete(id)
      if (res.code === 200) {
        message.success('删除成功')
        loadData()
      } else {
        message.error(res.message || '删除失败')
      }
    } catch (err) {
      message.error('删除失败')
    }
  }

  const handleToggleActive = async (record: DeliveryDriver) => {
    try {
      const res = await deliveryDriverApi.update(record.id, {
        is_active: record.is_active === 1 ? 0 : 1,
        zones: record.zones
      })
      if (res.code === 200) {
        message.success(record.is_active === 1 ? '已停用' : '已启用')
        loadData()
      }
    } catch (err) {
      message.error('操作失败')
    }
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60
    },
    {
      title: '配送员',
      key: 'name',
      width: 100,
      render: (_: any, record: DeliveryDriver) => record.employee?.real_name || '-'
    },
    {
      title: '手机号',
      key: 'phone',
      width: 120,
      render: (_: any, record: DeliveryDriver) => record.employee?.phone || '-'
    },
    {
      title: '配送区域',
      key: 'zones',
      render: (_: any, record: DeliveryDriver) => (
        <Space wrap>
          {record.zones?.map(z => (
            <Tag key={z.id} color="blue">{z.district_name}</Tag>
          )) || <span style={{ color: '#999' }}>未设置</span>}
        </Space>
      )
    },
    {
      title: '状态',
      key: 'is_active',
      width: 80,
      render: (_: any, record: DeliveryDriver) => (
        <Tag color={record.is_active === 1 ? 'green' : 'red'}>
          {record.is_active === 1 ? '启用' : '停用'}
        </Tag>
      )
    },
    {
      title: '企微ID',
      dataIndex: 'wecom_userid',
      key: 'wecom_userid',
      width: 120,
      render: (v: string) => v || '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_: any, record: DeliveryDriver) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleDetail(record)}>详情</Button>
          <Button type="link" size="small" onClick={() => handleEdit(record)}>编辑</Button>
          <Button type="link" size="small" onClick={() => handleToggleActive(record)}>
            {record.is_active === 1 ? '停用' : '启用'}
          </Button>
          <Popconfirm title="确认删除该配送员?" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  // Transfer 数据源
  const transferDataSource = SHANGHAI_DISTRICTS.map(d => ({
    key: d.code,
    title: d.name,
    description: d.name
  }))

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Space>
            <Input
              placeholder="搜索姓名/手机号"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onPressEnter={() => { setPage(1); loadData() }}
              style={{ width: 200 }}
              prefix={<SearchOutlined />}
            />
            <Button icon={<ReloadOutlined />} onClick={() => { setKeyword(''); setPage(1); loadData() }}>重置</Button>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增配送员</Button>
        </div>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps) }
          }}
        />
      </Card>

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editMode ? '编辑配送员' : '新增配送员'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={700}
        destroyOnClose
      >
        <Form form={form} labelCol={{ span: 6 }} wrapperCol={{ span: 16 }}>
          {!editMode && (
            <Form.Item name="employee_id" label="选择员工" rules={[{ required: true, message: '请选择员工' }]}>
              <Select
                placeholder="请选择员工"
                showSearch
                filterOption={(input, option) =>
                  (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {employees.map(emp => (
                  <Option key={emp.id} value={emp.id}>
                    {emp.real_name} ({emp.phone}) {emp.employee_no ? `[${emp.employee_no}]` : ''}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
          {editMode && (
            <Form.Item label="配送员">
              <span>{currentDriver?.employee?.real_name} ({currentDriver?.employee?.phone})</span>
            </Form.Item>
          )}
          <Form.Item name="wecom_userid" label="企业微信ID">
            <Input placeholder="用于推送配送通知（可选）" />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} placeholder="备注信息" />
          </Form.Item>
          <Form.Item label="配送区域" required>
            <Transfer
              dataSource={transferDataSource}
              titles={['可选区县', '已选区县']}
              targetKeys={selectedZones}
              onChange={(targetKeys) => setSelectedZones(targetKeys as string[])}
              render={item => item.title || ''}
              listStyle={{ width: 250, height: 300 }}
              showSearch
              filterOption={(input, item) => (item.title || '').toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情弹窗 */}
      <Modal
        title="配送员详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={600}
      >
        {currentDriver && (
          <Descriptions column={2} bordered>
            <Descriptions.Item label="姓名">{currentDriver.employee?.real_name}</Descriptions.Item>
            <Descriptions.Item label="手机号">{currentDriver.employee?.phone}</Descriptions.Item>
            <Descriptions.Item label="工号">{currentDriver.employee?.employee_no || '-'}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={currentDriver.is_active === 1 ? 'green' : 'red'}>
                {currentDriver.is_active === 1 ? '启用' : '停用'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="企业微信ID" span={2}>{currentDriver.wecom_userid || '-'}</Descriptions.Item>
            <Descriptions.Item label="配送区域" span={2}>
              <Space wrap>
                {currentDriver.zones?.map(z => (
                  <Tag key={z.id} color="blue">{z.district_name}</Tag>
                )) || <span style={{ color: '#999' }}>未设置</span>}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="备注" span={2}>{currentDriver.remark || '-'}</Descriptions.Item>
            <Descriptions.Item label="创建时间" span={2}>
              {currentDriver.created_at?.substring(0, 19).replace('T', ' ')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  )
}
